import { clsx } from "clsx";
import { useState } from "react";
import { Virtuoso } from "react-virtuoso";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import SearchInput from "common/components/SearchInput";
import Checkbox from "common/components/ui/Checkbox";
import {
  DropdownMenuCheckbox,
  DropdownMenuCount,
  DropdownMenuItem,
  DropdownMenuList,
} from "common/components/ui/DropdownMenu";
import { LABEL_COLORS, LEADS_FACETS } from "common/constants";
import useAllLabels from "common/datahooks/useAllLabels";
import useLeadsFacet from "common/datahooks/useLeadsFacet";
import useLeadsSearchParams from "common/hooks/useLeadsSearchParams";
import useSortedFacets from "common/hooks/useSortedFacets";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  FacetParam,
  Label,
  LeadsFacets,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import type { DripTargeting } from "../../../../../types";

function FacetValue({
  searchKey,
  facet,
  labels,
}: {
  searchKey: keyof Omit<DripTargeting, "keywords" | "favorite">;
  facet: FacetParam;
  labels: Label[];
}) {
  const { id, name } = facet;

  if (searchKey === "labels") {
    const { color } = labels.find(({ id: labelId }) => labelId === id) ?? {};

    return (
      <>
        <div
          className={clsx(
            "ml-2 size-3 shrink-0 rounded",
            color
              ? LABEL_COLORS[color].bgClassName
              : LABEL_COLORS.quaternary.bgClassName,
          )}
        />
        <span className="truncate text-start text-button-sm">{name}</span>
      </>
    );
  }

  return <span className="truncate text-start text-button-sm">{name}</span>;
}

interface DripFacetProps {
  facetKey: keyof LeadsFacets;
  searchKey: keyof Omit<DripTargeting, "keywords" | "favorite">;
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  setSearchParams: (
    searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>,
  ) => void;
}

export default function DripFacet({
  facetKey,
  searchKey,
  searchParams,
  setSearchParams,
}: DripFacetProps) {
  const { allLabels } = useAllLabels("workspace");

  const isDesktop = useTwBreakpoint("lg");
  const [inputValue, setInputValue] = useState("");

  const selectedFacets = searchParams[searchKey];
  const { toggleSearchParam } = useLeadsSearchParams(
    searchParams,
    setSearchParams,
  );
  const { facetData, isFetchingFacet } = useLeadsFacet(facetKey, searchParams);
  const sortedFacets = useSortedFacets(facetData || [], selectedFacets);
  const sortedAndFilteredFacets = sortedFacets.filter((facet) => {
    if (inputValue) {
      return facet.name.toLowerCase().includes(inputValue.toLowerCase());
    }

    return facet;
  });

  const listContent = sortedAndFilteredFacets.length ? (
    <Virtuoso
      style={{
        height: `min(${sortedAndFilteredFacets.length * 36}px, 220px)`,
      }}
      className="scrollbar-thin"
      totalCount={sortedAndFilteredFacets.length}
      itemContent={(index) => {
        const facet = sortedAndFilteredFacets[index];
        const { name, id, target_count: targetCount } = facet;

        const isSelected = selectedFacets.some((selected) =>
          Array.isArray(selected.id) && Array.isArray(id)
            ? selected.id.every((string) => id.includes(string))
            : selected.id === id,
        );

        if (isDesktop) {
          return (
            <DropdownMenuItem
              key={name}
              onSelect={() => toggleSearchParam(searchKey, facet)}
            >
              <DropdownMenuCheckbox
                isChecked={isSelected}
                onCheckedChange={() => toggleSearchParam(searchKey, facet)}
              />
              <FacetValue
                facet={facet}
                searchKey={searchKey}
                labels={allLabels}
              />
              <DropdownMenuCount>{targetCount}</DropdownMenuCount>
            </DropdownMenuItem>
          );
        }
        return (
          <button
            type="button"
            key={name}
            onClick={() => toggleSearchParam(searchKey, facet)}
            disabled={isFetchingFacet && !isSelected}
            className="flex w-full items-center gap-x-1.5 bg-white py-2 disabled:opacity-30 lg:px-4 lg:transition-colors lg:hover:bg-black-50"
          >
            <Checkbox checked={isSelected} isStyleOnly />

            <FacetValue
              facet={facet}
              searchKey={searchKey}
              labels={allLabels}
            />

            <span className="ml-auto text-black-400">{targetCount}</span>
          </button>
        );
      }}
    />
  ) : (
    <p className="px-4 py-8 text-center text-black-500">No results found</p>
  );

  const { label } = LEADS_FACETS[facetKey];

  return isDesktop ? (
    <>
      <MenuSearchInput
        placeholder={`Search ${label}`}
        value={inputValue}
        onChange={(e) => setInputValue(e.currentTarget.value)}
      />
      <DropdownMenuList>{listContent}</DropdownMenuList>
    </>
  ) : (
    <>
      <SearchInput
        className="mb-2"
        variant="md"
        value={inputValue}
        placeholder={`Search ${label}`}
        onChange={(e) => setInputValue(e.currentTarget.value)}
        onClear={() => setInputValue("")}
      />
      <div className="flex flex-col">{listContent}</div>
    </>
  );
}
